import {
  AuthEvent,
  AuthPlatformDef,
  HoppUser,
} from "@hoppscotch/common/platform/auth"
import {
  Subscription,
  BehaviorSubject,
  Subject,
  filter,
  map,
  combineLatest,
  firstValueFrom,
} from "rxjs"
import {
  setDoc,
  onSnapshot,
  updateDoc,
  doc,
  getFirestore,
} from "firebase/firestore"
import { FirebaseError } from "firebase/app"
import {
  AuthCredential,
  User as FBUser,
  sendSignInLinkToEmail,
  linkWithCredential,
  getAuth,
  ActionCodeSettings,
  isSignInWithEmailLink as isSignInWithEmailLinkFB,
  signInWithEmailLink as signInWithEmailLinkFB,
  sendEmailVerification,
  signInWithPopup,
  GoogleAuthProvider,
  GithubAuthProvider,
  OAuthProvider,
  fetchSignInMethodsForEmail,
  updateProfile,
  reauthenticateWithCredential,
  onAuthStateChanged,
  onIdTokenChanged,
  signOut,
  UserCredential,
  getAdditionalUserInfo,
  verifyBeforeUpdateEmail,
} from "firebase/auth"
import { getService } from "@hoppscotch/common/modules/dioc"
import { PersistenceService } from "@services/persistence"

import * as E from "fp-ts/Either"
import { subscribeToNewsletter } from "../../../newsletter"
import {
  getOrganizationDomain,
  getRootDomain,
  isDefaultCloudInstance,
} from "../../../helpers/utils/cloud-for-orgs"

export const currentUserFB$ = new BehaviorSubject<FBUser | null>(null)
export const authEvents$ = new Subject<AuthEvent>()
export const probableUser$ = new BehaviorSubject<HoppUser | null>(null)

export const authIdToken$ = new BehaviorSubject<string | null>(null)

const persistenceService = getService(PersistenceService)

async function signInWithEmailLink(email: string, url: string) {
  return await signInWithEmailLinkFB(getAuth(), email, url)
}

function fbUserToHoppUser(user: FBUser): HoppUser {
  return {
    uid: user.uid,
    displayName: user.displayName,
    email: user.email,
    photoURL: user.photoURL,
    emailVerified: user.emailVerified,
  }
}

const currentUser$ = new BehaviorSubject<HoppUser | null>(null)

const subscribeToNewsletterWithCredentials = async (
  credentials: UserCredential
) => {
  const additionalInfo = getAdditionalUserInfo(credentials)

  if (!additionalInfo) {
    return
  }

  // wait for the id token to be available
  const authIDToken = await firstValueFrom(
    authIdToken$.pipe(filter((x) => !!x))
  )

  if (additionalInfo.isNewUser && authIDToken) {
    await subscribeToNewsletter(authIDToken)
  }
}

async function signInUserWithGithubFB() {
  return await signInWithPopup(
    getAuth(),
    new GithubAuthProvider().addScope("gist")
  )
}

async function signInUserWithGoogleFB() {
  return await signInWithPopup(getAuth(), new GoogleAuthProvider())
}

async function signInUserWithMicrosoftFB() {
  return await signInWithPopup(getAuth(), new OAuthProvider("microsoft.com"))
}

async function setUser(user: HoppUser | null) {
  currentUser$.next(user)
  probableUser$.next(user)

  await persistenceService.setLocalConfig("login_state", JSON.stringify(user))
}

/**
 * Reauthenticate the user with the given credential
 */
async function reauthenticateUser() {
  if (!currentUserFB$.value || !currentUser$.value)
    throw new Error("No user has logged in")

  const currentAuthMethod = currentUser$.value.provider

  let credential
  if (currentAuthMethod === "google.com") {
    // const result = await signInUserWithGithubFB()
    const result = await signInUserWithGoogleFB()
    credential = GithubAuthProvider.credentialFromResult(result)
  } else if (currentAuthMethod === "github.com") {
    // const result = await signInUserWithGoogleFB()
    const result = await signInUserWithGithubFB()
    credential = GoogleAuthProvider.credentialFromResult(result)
  } else if (currentAuthMethod === "microsoft.com") {
    const result = await signInUserWithMicrosoftFB()
    credential = OAuthProvider.credentialFromResult(result)
  } else if (currentAuthMethod === "password") {
    const email = prompt(
      "Reauthenticate your account using your current email:"
    )

    await def
      .signInWithEmail(email as string)
      .then(() =>
        alert(
          `Check your inbox - we sent an email to ${email}. It contains a magic link that will reauthenticate your account.`
        )
      )
      .catch((e) => {
        alert(`Error: ${e.message}`)
        console.error(e)
      })
    return
  }
  try {
    await reauthenticateWithCredential(
      currentUserFB$.value,
      credential as AuthCredential
    )
  } catch (e) {
    console.error("error updating", e)
    throw e
  }
}

/**
 * Links account with another account given in a auth/account-exists-with-different-credential error
 *
 * @param error - Error caught after trying to login
 *
 * @returns Promise of UserCredential
 */
async function linkWithFBCredentialFromAuthError(error: unknown) {
  if (!(error instanceof FirebaseError)) {
    throw error
  }

  // credential is not null since this function is called after an auth/account-exists-with-different-credential error, ie credentials actually exist
  const credentials = OAuthProvider.credentialFromError(error)
  const email = (error.customData?.email as string | undefined) ?? null

  if (!credentials || !email) {
    throw error
  }

  const otherLinkedProviders = (await getSignInMethodsForEmail(email)).filter(
    (providerId) => credentials.providerId !== providerId
  )

  let user: FBUser | null = null

  if (otherLinkedProviders.indexOf("google.com") >= -1) {
    user = (await signInUserWithGoogleFB()).user
  } else if (otherLinkedProviders.indexOf("github.com") >= -1) {
    user = (await signInUserWithGithubFB()).user
  } else if (otherLinkedProviders.indexOf("microsoft.com") >= -1) {
    user = (await signInUserWithMicrosoftFB()).user
  }

  // user is not null since going through each provider will return a user
  return await linkWithCredential(user!, credentials)
}

async function setProviderInfo(id: string, token: string) {
  if (!currentUser$.value) throw new Error("No user has logged in")

  const us = {
    updatedOn: new Date(),
    provider: id,
    accessToken: token,
  }

  try {
    await updateDoc(doc(getFirestore(), "users", currentUser$.value.uid), us)
  } catch (e) {
    console.error("error updating provider info", e)
    throw e
  }
}

async function getSignInMethodsForEmail(email: string) {
  return await fetchSignInMethodsForEmail(getAuth(), email)
}

export function getUserFirebaseMetadata() {
  return currentUserFB$.value?.metadata
}

export const def: AuthPlatformDef = {
  getCurrentUserStream: () => currentUser$,
  getAuthEventsStream: () => authEvents$,
  getProbableUserStream: () => probableUser$,

  getCurrentUser: () => currentUser$.value,
  getProbableUser: () => probableUser$.value,

  getBackendHeaders() {
    return {
      authorization: `Bearer ${authIdToken$.value}`,
    }
  },
  axiosPlatformConfig() {
    return {
      headers: {
        Authorization: `Bearer ${authIdToken$.value}`,
      },
    }
  },
  willBackendHaveAuthError() {
    return !authIdToken$.value
  },
  onBackendGQLClientShouldReconnect(func) {
    authIdToken$.subscribe(() => {
      func()
    })
  },
  getDevOptsBackendIDToken() {
    return authIdToken$.value
  },
  async performAuthInit() {
    // todo: implement
    const auth = getAuth()
    const firestore = getFirestore()

    combineLatest([currentUserFB$, authIdToken$])
      .pipe(
        map(([user, token]) => {
          // If there is no auth token, we will just consider as the auth as not complete
          if (token === null) return null
          if (user !== null) return fbUserToHoppUser(user)
          return null
        })
      )
      .subscribe((x) => {
        currentUser$.next(x)
      })

    let extraSnapshotStop: (() => void) | null = null

    probableUser$.next(
      JSON.parse(
        (await persistenceService.getLocalConfig("login_state")) ?? "null"
      )
    )

    onAuthStateChanged(auth, async (user) => {
      const wasLoggedIn = currentUser$.value !== null

      if (user) {
        probableUser$.next(user)
      } else {
        probableUser$.next(null)
        await persistenceService.removeLocalConfig("login_state")
      }

      if (!user && extraSnapshotStop) {
        extraSnapshotStop()
        extraSnapshotStop = null
      } else if (user) {
        // Merge all the user info from all the authenticated providers
        user.providerData.forEach((profile) => {
          if (!profile) return

          const us = {
            updatedOn: new Date(),
            provider: profile.providerId,
            name: profile.displayName,
            email: profile.email,
            photoUrl: profile.photoURL,
            uid: profile.uid,
          }

          setDoc(doc(firestore, "users", user.uid), us, { merge: true }).catch(
            (e) => console.error("error updating", us, e)
          )
        })

        extraSnapshotStop = onSnapshot(
          doc(firestore, "users", user.uid),
          (doc) => {
            const data = doc.data()

            const userUpdate: HoppUser = fbUserToHoppUser(user)

            if (data) {
              // Write extra provider data
              userUpdate.provider = data.provider
              userUpdate.accessToken = data.accessToken
            }

            currentUser$.next(userUpdate)
          }
        )
      }

      currentUserFB$.next(user)
      currentUser$.next(user === null ? null : fbUserToHoppUser(user))

      // User wasn't found before, but now is there (login happened)
      if (!wasLoggedIn && user) {
        authEvents$.next({
          event: "login",
          user: currentUser$.value!,
        })
      } else if (wasLoggedIn && !user) {
        // User was found before, but now is not there (logout happened)
        authEvents$.next({
          event: "logout",
        })
      }
    })

    onIdTokenChanged(auth, async (user) => {
      if (user) {
        authIdToken$.next(await user.getIdToken())

        await persistenceService.setLocalConfig(
          "login_state",
          JSON.stringify(user)
        )
      } else {
        authIdToken$.next(null)
      }
    })
  },

  waitProbableLoginToConfirm() {
    return new Promise<void>((resolve, reject) => {
      if (authIdToken$.value) resolve()

      if (!probableUser$.value) reject(new Error("no_probable_user"))

      let sub: Subscription | null = null
      sub = authIdToken$.pipe(filter((token) => !!token)).subscribe(() => {
        sub?.unsubscribe()
        resolve()
      })
    })
  },

  async signInWithEmail(email: string) {
    const subdomain = getOrganizationDomain()

    const continueUrl = new URL(`${import.meta.env.VITE_BASE_URL}/enter`)

    if (!isDefaultCloudInstance) {
      const rootDomain = getRootDomain()

      continueUrl.searchParams.set(
        "redirect",
        `${subdomain}.${rootDomain}/enter`
      )
    }

    const EMAIL_ACTION_CODE_SETTINGS: ActionCodeSettings = {
      url: continueUrl.href,
      handleCodeInApp: true,
    }

    const persistenceService = getService(PersistenceService)

    await persistenceService.setLocalConfig(
      "email_redirect_url",
      window.location.href
    )

    return await sendSignInLinkToEmail(
      getAuth(),
      email,
      EMAIL_ACTION_CODE_SETTINGS
    )
  },

  isSignInWithEmailLink(url: string) {
    return isSignInWithEmailLinkFB(getAuth(), url)
  },

  async verifyEmailAddress() {
    if (!currentUserFB$.value) throw new Error("No user has logged in")

    try {
      await sendEmailVerification(currentUserFB$.value)
    } catch (e) {
      console.error("error verifying email address", e)
      throw e
    }
  },
  async signInUserWithGoogle() {
    const creds = await signInUserWithGoogleFB()
    await subscribeToNewsletterWithCredentials(creds)
  },
  async signInUserWithGithub() {
    try {
      const cred = await signInUserWithGithubFB()
      const oAuthCred = GithubAuthProvider.credentialFromResult(cred)!
      const token = oAuthCred.accessToken
      await setProviderInfo(cred.providerId!, token!)

      await subscribeToNewsletterWithCredentials(cred)

      return {
        type: "success",
        user: fbUserToHoppUser(cred.user),
      }
    } catch (e) {
      console.error("error while logging in with github", e)

      if (
        e instanceof FirebaseError &&
        e.code === "auth/account-exists-with-different-credential"
      ) {
        return {
          type: "account-exists-with-different-cred",
          link: async () => {
            await linkWithFBCredentialFromAuthError(e)
          },
        }
      } else {
        return {
          type: "error",
          err: e,
        }
      }
    }
  },
  async signInUserWithMicrosoft() {
    const creds = await signInUserWithMicrosoftFB()
    await subscribeToNewsletterWithCredentials(creds)
  },
  async signInWithEmailLink(email: string, url: string) {
    const creds = await signInWithEmailLinkFB(getAuth(), email, url)
    await subscribeToNewsletterWithCredentials(creds)
  },
  async setEmailAddress(email: string) {
    if (!currentUserFB$.value)
      return {
        type: "error",
        err: "no-user-logged-in",
      }

    try {
      await verifyBeforeUpdateEmail(currentUserFB$.value, email)
      return {
        type: "success",
      }
    } catch (e) {
      if (
        e instanceof FirebaseError &&
        e.code === "auth/requires-recent-login"
      ) {
        await reauthenticateUser()
        // Retry setting the email after reauthentication
        return {
          type: "requires-recent-login",
          link: async () => {
            await def.setEmailAddress(email)
          },
        }
      } else if (
        e instanceof FirebaseError &&
        e.code === "auth/email-already-in-use"
      ) {
        return {
          type: "email-already-in-use",
          err: e,
        }
      }

      console.error("error setting email address", e)
      return {
        type: "error",
        err: e,
      }
    }
  },
  async setDisplayName(name: string) {
    if (!name) return E.left("USER_NAME_CANNOT_BE_EMPTY")
    if (!currentUserFB$.value) return E.left("NO_USER_LOGGED_IN")

    const us = {
      displayName: name,
    }

    try {
      await updateProfile(currentUserFB$.value, us)
      setUser({
        ...fbUserToHoppUser(currentUserFB$.value),
        displayName: name,
      })
      return E.right(undefined)
    } catch (e) {
      console.error("error updating display name", e)
      return E.left(e)
    }
  },
  async signOutUser() {
    if (!currentUser$.value) throw new Error("No user has logged in")

    await signOut(getAuth())
  },
  async processMagicLink() {
    const persistenceService = getService(PersistenceService)

    if (this.isSignInWithEmailLink(window.location.href)) {
      let email = await persistenceService.getLocalConfig("emailForSignIn")

      if (!email) {
        email = window.prompt(
          "Please provide your email for confirmation"
        ) as string
      }

      // TODO: Account for subdomain instances while generating the URL
      await signInWithEmailLink(email, window.location.href)

      await persistenceService.removeLocalConfig("emailForSignIn")

      const returnURL =
        (await persistenceService.getLocalConfig("email_redirect_url")) ?? "/"

      await persistenceService.removeLocalConfig("email_redirect_url")

      window.location.href = returnURL
    }
  },
  getAllowedAuthProviders() {
    return Promise.resolve(E.right(["GITHUB", "GOOGLE", "MICROSOFT", "EMAIL"]))
  },

  /**
   * This is used to determine whether the email address is editable by the user or not.
   * This is enabled only for the cloud instance.
   */
  isEmailEditable: true,
}
